import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { NotebookPen } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { VoiceAssistantTextarea } from '@/components/ui/VoiceAssistantTextarea';
import { CrmRecordAutocomplete } from '@/components/ui/crm-record-autocomplete';
import { blockNavigationDuringSave, useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { apiRequest } from '@/lib/queryClient';
import { apiClient } from '@/lib/apiClient';
import { refreshUserNoteViews } from '@/lib/userNoteCache';
import { useToast } from '@/hooks/use-toast';
import {
  OPEN_QUICK_NOTE_EVENT,
  NOTE_ENTITY_TYPES,
  quickNoteText,
  resolveNoteEntityContextFromPath,
  validNoteEntityContext,
  type NoteEntityContext,
  type QuickNoteComposerDetail,
  type QuickNoteFileSource,
} from '@/lib/noteEntityContext';
import type { MartinaTerminalContext } from '@/components/exai/ExAiGlobalDrawer';
import { quickNoteDraftOwner, readQuickNoteDraft, writeQuickNoteDraft, removeQuickNoteDraft, quickNoteDraftKey, type QuickNoteDraft } from '@/lib/quickNoteDraft';
import { resolveTravelNoteTarget, validTravelNoteItemId } from '@/lib/quickTravelNoteContext';

const ENTITY_LABEL: Record<NoteEntityContext['entityType'], string> = {
  contact: 'Contatto',
  company: 'Azienda',
  deal: 'Deal',
  lead: 'Lead',
  branch: 'Filiale',
};

type QuickNoteWallet = {
  id: number;
  title: string;
  status: string;
  items: { id: number; title: string }[];
};

/**
 * Global Note rapide composer. Entity notes append through /api/notes;
 * travel notes and to-dos use the owner-scoped Wallet entries endpoint.
 */
export function QuickNoteDialog() {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const currentOwner = () => {
    try { return quickNoteDraftOwner(queryClient.getQueryData(['/api/auth/me']), window.localStorage.getItem('auth_token')); }
    catch { return null; }
  };
  const [ownerId, setOwnerId] = useState<number | null>(currentOwner);
  const ownerRef = useRef(ownerId);
  const [pendingSave, setPendingSave] = useState<QuickNoteDraft['pendingSave']>(null);
  const [saveConflict, setSaveConflict] = useState(false);
  const [recovery, setRecovery] = useState<QuickNoteDraft | null>(null);
  const [recoveryError, setRecoveryError] = useState(false);
  const [storageError, setStorageError] = useState(false);
  const externalDraftConflictRef = useRef(false);
  const [draftUpdatedAt, setDraftUpdatedAt] = useState<string | null>(null);
  const draftIdRef = useRef<string | null>(null);
  const pendingOpenRef = useRef<QuickNoteComposerDetail | null>(null);

  const [open, setOpen] = useState(false);
  const [context, setContext] = useState<NoteEntityContext | null>(null);
  const [entityType, setEntityType] = useState<NoteEntityContext['entityType']>('contact');
  const [sourceFile, setSourceFile] = useState<QuickNoteFileSource | null>(null);
  const [startVoice, setStartVoice] = useState(false);
  const [captureSession, setCaptureSession] = useState(0);
  const [draft, setDraft] = useState('');
  const [historyText, setHistoryText] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const captureSessionRef = useRef(0);
  const [notebookMode, setNotebookMode] = useState(false);
  const [travelMode, setTravelMode] = useState(false);
  const [wallets, setWallets] = useState<QuickNoteWallet[]>([]);
  const [walletId, setWalletId] = useState<number | null>(null);
  const [travelKind, setTravelKind] = useState<'note' | 'todo'>('note');
  const [travelItemId, setTravelItemId] = useState<number | null>(null);
  const [travelError, setTravelError] = useState('');
  const [needsTravelTargetChoice, setNeedsTravelTargetChoice] = useState(false);
  const { requestClose, closeAfterSave } = useUnsavedChangesGuard(open, Boolean(draft.trim()) && (storageError || !ownerId), setOpen);

  const requestSafeClose = (nextOpen: boolean) => {
    if (!nextOpen && savingRef.current) {
      toast({ title: 'Salvataggio in corso', description: 'Attendi l’esito prima di chiudere la nota.' });
      return false;
    }
    if (!nextOpen && draft.trim() && storageError) {
      toast({ title: 'Bozza non protetta', description: 'La bozza sul dispositivo non è aggiornata o è cambiata in un’altra finestra. Copia il testo o riprova prima di chiudere.', variant: 'destructive' });
      return false;
    }
    const accepted = requestClose(nextOpen);
    if (!nextOpen && accepted) captureSessionRef.current++;
    return accepted;
  };

  const persistDraft = (attempt = pendingSave) => {
    if (!ownerId || ownerId !== currentOwner() || recovery || recoveryError || !draftIdRef.current || externalDraftConflictRef.current) return false;
    const saved: QuickNoteDraft = { version: 1, ownerId, id: draftIdRef.current, updatedAt: new Date().toISOString(), text: draft,
      context, entityType, sourceFile, notebookMode, travelMode, walletId, travelKind, travelItemId, pendingSave: attempt };
    let stored = false;
    try { stored = writeQuickNoteDraft(saved, window.localStorage); } catch { /* Browser storage can be disabled. */ }
    setStorageError(!stored); setDraftUpdatedAt(saved.updatedAt); return stored;
  };
  useEffect(() => {
    if (open && !recovery && !recoveryError && (draft.trim() || draftUpdatedAt)) persistDraft();
  }, [draft, context, entityType, sourceFile, notebookMode, travelMode, walletId, travelKind, travelItemId, open]);

  useEffect(() => {
    const checkOwner = (event?: StorageEvent | Event) => {
      if (event instanceof StorageEvent && ownerRef.current && event.key === quickNoteDraftKey(ownerRef.current) && draftIdRef.current) {
        externalDraftConflictRef.current = true; setStorageError(true);
      }
      const next = currentOwner();
      if (next === ownerRef.current) return;
      ownerRef.current = next;
      captureSessionRef.current++;
      setStartVoice(false); setOpen(false); setDraft(''); setRecovery(null); setRecoveryError(false);
      setSourceFile(null); setContext(null); setWallets([]); setWalletId(null); setTravelItemId(null);
      setDraftUpdatedAt(null); draftIdRef.current = null; externalDraftConflictRef.current = false; setPendingSave(null); setSaveConflict(false); setOwnerId(next);
    };
    const unsubscribe = queryClient.getQueryCache().subscribe(() => checkOwner());
    window.addEventListener('storage', checkOwner); window.addEventListener('focus', checkOwner);
    return () => { unsubscribe(); window.removeEventListener('storage', checkOwner); window.removeEventListener('focus', checkOwner); };
  }, [queryClient]);

  const clearDeviceDraft = () => {
    if (!ownerId || ownerId !== currentOwner()) return !ownerId;
    try {
      if (!removeQuickNoteDraft(ownerId, window.localStorage)) throw new Error('storage unavailable');
      setDraftUpdatedAt(null); draftIdRef.current = null; externalDraftConflictRef.current = false; setPendingSave(null); setSaveConflict(false); setStorageError(false); return true;
    } catch {
      setStorageError(true);
      toast({ title: 'Bozza ancora sul dispositivo', description: 'Non riesco a rimuoverla. Non inviarla di nuovo se la nota è già stata salvata nel CRM.', variant: 'destructive' });
      return false;
    }
  };

  const resumeDraft = () => {
    if (!recovery || recovery.ownerId !== currentOwner()) return;
    captureSessionRef.current++;
    const session = captureSessionRef.current;
    setCaptureSession(session); setStartVoice(false); draftIdRef.current = recovery.id;
    setDraft(recovery.text); setContext(recovery.context); setEntityType(recovery.entityType); setSourceFile(recovery.sourceFile);
    setNotebookMode(Boolean(recovery.notebookMode)); setTravelMode(recovery.travelMode); setWalletId(recovery.walletId); setTravelKind(recovery.travelKind); setTravelItemId(recovery.travelItemId);
    setDraftUpdatedAt(recovery.updatedAt); setPendingSave(recovery.pendingSave ?? null); setSaveConflict(Boolean(recovery.pendingSave?.conflict)); setNeedsTravelTargetChoice(false); setTravelError('');
    if (recovery.travelMode) {
      const restored = recovery;
      setWallets([]); setNeedsTravelTargetChoice(true);
      void apiClient('/api/travel/wallets').then((result: { wallets?: QuickNoteWallet[] }) => {
        if (captureSessionRef.current !== session || restored.ownerId !== currentOwner()) return;
        const available = result.wallets ?? []; setWallets(available);
        const wallet = available.find((item) => item.id === restored.walletId);
        const valid = wallet && (restored.travelItemId === null || validTravelNoteItemId(wallet.items, restored.travelItemId) !== null);
        setNeedsTravelTargetChoice(!valid);
        if (!valid) setTravelError('La destinazione della bozza non è più disponibile. Scegli il Wallet e la tappa.');
      }).catch(() => { if (captureSessionRef.current === session) setTravelError('Non riesco a verificare il Wallet. La bozza è conservata; riprova più tardi.'); });
    }
    setRecovery(null); setRecoveryError(false);
  };

  useEffect(() => {
    const onOpen = (event: Event) => {
      if (savingRef.current) {
        toast({ title: 'Salvataggio in corso', description: 'Attendi l’esito prima di aprire un’altra nota.' });
        return;
      }
      if (open && draft.trim() && storageError) { requestSafeClose(false); return; }
      const detail = (event as CustomEvent<QuickNoteComposerDetail | null>).detail;
      setHistoryText(typeof detail?.historyText === 'string' ? detail.historyText : null);
      const verifiedOwner = currentOwner();
      if (!verifiedOwner && open && draft.trim() && !window.confirm('La nota non è protetta sul dispositivo. Vuoi sostituirla?')) return;
      if (ownerRef.current !== verifiedOwner) {
        ownerRef.current = verifiedOwner; setOwnerId(verifiedOwner); setDraft(''); setRecovery(null);
      }
      if (verifiedOwner) {
        let found: ReturnType<typeof readQuickNoteDraft>;
        try { found = readQuickNoteDraft(verifiedOwner, window.localStorage); } catch { found = { draft: null, error: true }; }
        if (found.draft || found.error) {
          pendingOpenRef.current = detail ?? null;
          captureSessionRef.current++; setStartVoice(false); setRecovery(found.draft); setRecoveryError(found.error && !found.draft);
          setStorageError(found.error); setOpen(true); return;
        }
      }
      setRecovery(null); setRecoveryError(false); setStorageError(false); setDraftUpdatedAt(null); setPendingSave(null); setSaveConflict(false);
      draftIdRef.current = crypto.randomUUID();
      captureSessionRef.current++;
      const openedSession = captureSessionRef.current;
      setCaptureSession(openedSession);
      setStartVoice(Boolean(detail?.startVoice));
      if (window.location.pathname === '/travel') {
        setNotebookMode(false);
        setTravelMode(true);
        setContext(null);
        setSourceFile(null);
        setDraft('');
        setTravelError('');
        setTravelKind('note');
        setTravelItemId(null);
        setNeedsTravelTargetChoice(false);
        setWalletId(null);
        setWallets([]);
        setOpen(true);
        void apiClient('/api/travel/wallets').then((result: { wallets?: QuickNoteWallet[] }) => {
          if (captureSessionRef.current !== openedSession) return;
          const available = result.wallets ?? [];
          const target = resolveTravelNoteTarget(available, window.location.search);
          setWallets(available);
          setWalletId(target.walletId);
          setTravelItemId(target.itemId);
        }).catch(() => {
          if (captureSessionRef.current === openedSession) setTravelError('Non riesco a caricare i Wallet. Riprova più tardi.');
        });
        return;
      }
      const fromDetail = validNoteEntityContext(detail);
      const resolved = fromDetail ?? resolveNoteEntityContextFromPath(window.location.pathname);
      const rawSource = detail?.sourceFile;
      const validTargets = rawSource?.targets.flatMap((target) => {
        const valid = validNoteEntityContext(target);
        return valid ? [{ ...valid, label: target.label }] : [];
      }) ?? [];
      const file = rawSource && Number.isSafeInteger(rawSource.id) && rawSource.id > 0
        ? { id: rawSource.id, name: rawSource.name.replace(/\s+/g, ' ').trim().slice(0, 200), targets: validTargets }
        : null;
      const selected = fromDetail ?? (file ? validTargets.length === 1 ? validTargets[0] : null : resolved);
      setNotebookMode(!selected && !file?.targets.length);
      setContext(selected);
      setEntityType(selected?.entityType ?? validTargets[0]?.entityType ?? 'contact');
      setSourceFile(file);
      setTravelMode(false);
      setDraft('');
      setOpen(true);
    };
    document.addEventListener(OPEN_QUICK_NOTE_EVENT, onOpen);
    return () => document.removeEventListener(OPEN_QUICK_NOTE_EVENT, onOpen);
  }, [draft, open, toast, ownerId, storageError]);

  const handleSave = async () => {
    if (recovery || recoveryError || saveConflict || ownerId !== currentOwner()) return;
    const text = quickNoteText(draft, travelMode ? null : sourceFile);
    if (!travelMode && !pendingSave && text.length > 20_000) {
      toast({ title: 'Nota troppo lunga', description: 'La nota, compreso il riferimento al file, può contenere fino a 20.000 caratteri. Riduci il testo prima di salvarlo; la bozza resta disponibile.', variant: 'destructive' });
      return;
    }
    if (!draft.trim() || (!notebookMode && !context && !walletId) || savingRef.current || (travelMode && needsTravelTargetChoice)) return;
    if (travelMode) {
      const selectedWallet = wallets.find((wallet) => wallet.id === walletId);
      if (!selectedWallet || (travelItemId !== null
        && validTravelNoteItemId(selectedWallet.items, travelItemId) === null)) {
        setNeedsTravelTargetChoice(true);
        setTravelError('Il Wallet o la tappa non sono più disponibili. Scegli di nuovo la destinazione della nota.');
        return;
      }
    }
    const attempt: QuickNoteDraft['pendingSave'] = !travelMode && (notebookMode || context) ? pendingSave ?? { clientKey: draftIdRef.current ?? crypto.randomUUID(), text, context: notebookMode ? null : context, destination: notebookMode ? 'notebook' : 'record' } : null;
    if (attempt && !persistDraft(attempt)) {
      toast({ title: 'Salvataggio non avviato', description: 'Prima proteggi la bozza sul dispositivo. Il testo è rimasto qui.', variant: 'destructive' });
      return;
    }
    if (attempt) setPendingSave(attempt);
    const savingSession = captureSessionRef.current;
    savingRef.current = true;
    const releaseNavigationBlock = blockNavigationDuringSave();
    setIsSaving(true);
    try {
      if (travelMode && walletId) {
        await apiClient(`/api/travel/wallets/${walletId}/entries`, {
          method: 'POST',
          body: JSON.stringify({ kind: travelKind, text, itemId: travelItemId }),
        });
        await queryClient.invalidateQueries({ queryKey: ['/api/travel/wallets'] });
        if (savingSession === captureSessionRef.current) {
          clearDeviceDraft();
          setDraft('');
          closeAfterSave();
        }
        toast({ title: travelKind === 'todo' ? 'Da fare aggiunto al Wallet' : 'Nota aggiunta al Wallet' });
        return;
      }
      if (!attempt) return;
      const isNotebook = attempt.destination === 'notebook';
      if (!isNotebook && !attempt.context) return;
      const result = await apiRequest(isNotebook ? '/api/exai/voice-memo' : `/api/notes/${attempt.context!.entityType}/${attempt.context!.entityId}`, {
        method: 'POST',
        data: isNotebook ? { transcript: attempt.text, clientKey: attempt.clientKey } : { text: attempt.text, clientKey: attempt.clientKey },
        notifySuccess: false,
      });
      const nextNotes = String(result?.notes_user ?? '');
      if (ownerId === currentOwner()) {
        if (isNotebook) void queryClient.invalidateQueries({ queryKey: ['/api/exai/voice-memo', ownerId] });
        else if (attempt.context) refreshUserNoteViews(queryClient, attempt.context.entityType, attempt.context.entityId, nextNotes, result.updatedAt);
      }
      if (savingSession === captureSessionRef.current) {
        clearDeviceDraft();
        setDraft('');
        closeAfterSave();
      }
      toast({ title: isNotebook ? 'Nota salvata nel Taccuino' : 'Nota aggiunta', ...(isNotebook ? { description: 'La ritrovi in Taccuino Martina. Nessuna azione è stata eseguita dal testo.' } : {}) });
    } catch (error) {
      console.error('Errore Note rapide:', error);
      if (savingSession === captureSessionRef.current && ownerId === currentOwner() && !travelMode && attempt && ['NOTE_IDEMPOTENCY_CONFLICT', 'VOICE_MEMO_IDEMPOTENCY_CONFLICT'].includes((error as { code?: string })?.code ?? '')) {
        const conflict = { ...attempt, conflict: true }; setPendingSave(conflict); setSaveConflict(true); persistDraft(conflict);
      }
      if (travelMode && savingSession === captureSessionRef.current) {
        // A reservation can be replaced while this drawer is open. Refresh the
        // canonical Wallet list after a rejected write and require an explicit
        // target choice rather than silently attaching the note to the journey.
        try {
          const result = await apiClient('/api/travel/wallets') as { wallets?: QuickNoteWallet[] };
          if (savingSession !== captureSessionRef.current || ownerId !== currentOwner()) return;
          const available = result.wallets ?? [];
          setWallets(available);
          const selectedWallet = available.find((wallet) => wallet.id === walletId);
          if (!selectedWallet || (travelItemId !== null
            && validTravelNoteItemId(selectedWallet.items, travelItemId) === null)) {
            setTravelItemId(null);
            setNeedsTravelTargetChoice(true);
            setTravelError('Il Wallet o la tappa sono cambiati. Scegli di nuovo la destinazione della nota.');
          }
        } catch {
          setTravelError('Non riesco a rileggere il Wallet. Il testo è rimasto qui: riprova più tardi.');
        }
      }
      toast({ title: travelMode ? 'Impossibile salvare nel Wallet' : 'Impossibile aggiungere la nota', variant: 'destructive' });
    } finally {
      releaseNavigationBlock();
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={requestSafeClose}>
      <SheetContent
        side="right"
        className={`flex h-full w-full flex-col gap-0 overflow-hidden rounded-l-2xl border-l border-border bg-card p-0 ${historyText !== null ? 'sm:max-w-[560px]' : 'sm:max-w-[66vw]'}`}
        data-testid="quick-note-drawer"
      >
        <SheetHeader className="shrink-0 border-b border-border px-6 py-5 pr-16">
          <SheetTitle className="flex items-center gap-2">
            <NotebookPen className="h-5 w-5 text-emerald-700" aria-hidden="true" />
            Note rapide
          </SheetTitle>
          <SheetDescription>
            {travelMode ? 'Scegli il Wallet e, se serve, la singola tappa. La nota resta consultabile anche dopo la chiusura del viaggio.'
              : 'Detta o scrivi, controlla il testo e scegli dove salvarlo. Le note generiche restano nel Taccuino Martina.'}
          </SheetDescription>
          <Button type="button" variant="link" className="h-auto justify-start p-0 text-sm" disabled={isSaving} onClick={() => { if (requestSafeClose(false)) navigate('/notes'); }}>Apri Taccuino Martina</Button>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {(recovery || recoveryError) ? <section className="space-y-4" aria-label="Recupero bozza nota">
            <h3 className="font-medium">Hai una bozza sul dispositivo</h3>
            <p className="text-sm">Riprendila con la sua destinazione originale. {recovery?.pendingSave ? 'Il precedente salvataggio nel CRM è da verificare.' : 'Non è stata salvata nel CRM.'} Nessun invio parte automaticamente.</p>
            {recovery && <><p className="text-xs text-muted-foreground">{new Date(recovery.updatedAt).toLocaleString('it-IT')} · {recovery.notebookMode ? 'Taccuino Martina' : recovery.travelMode ? `Wallet #${recovery.walletId ?? 'da scegliere'}` : recovery.context ? `${ENTITY_LABEL[recovery.context.entityType]} #${recovery.context.entityId}` : 'Destinazione da scegliere'}</p><p className="max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded border p-3 text-sm">{recovery.text}</p></>}
            {recoveryError && <p role="alert">La bozza non è leggibile. Il contenuto sul dispositivo è stato conservato.</p>}
            <div className="flex flex-wrap gap-2"><Button disabled={!recovery} onClick={resumeDraft}>Riprendi bozza</Button><Button variant="outline" onClick={() => {
              if (!window.confirm(recovery?.pendingSave ? 'Questa nota potrebbe essere già salvata nel CRM. Eliminare la bozza dal dispositivo non annulla quel salvataggio. Continuare?' : 'Eliminare definitivamente questa bozza dal dispositivo?')) return;
              if (!clearDeviceDraft()) return;
              setRecovery(null); setRecoveryError(false); setDraft(''); setOpen(false);
              document.dispatchEvent(new CustomEvent(OPEN_QUICK_NOTE_EVENT, { detail: { ...pendingOpenRef.current, startVoice: false } }));
            }}>Elimina bozza</Button></div>
          </section> : <>
          <p className="mb-3 text-xs text-muted-foreground" role="status">{storageError ? 'Bozza non aggiornata sul dispositivo: spazio non disponibile o modifica da un’altra finestra. Il testo resta qui; copialo prima di uscire.' : ownerId ? pendingSave ? 'Bozza sul dispositivo · esito del salvataggio da verificare' : draftUpdatedAt ? 'Bozza sul dispositivo · non ancora salvata nel CRM' : 'La bozza resterà su questo dispositivo.' : 'Identità non verificata: la bozza resta solo in questa schermata.'}</p>
          {storageError && <Button variant="outline" className="mb-3" onClick={() => persistDraft()}>Riprova protezione bozza</Button>}
          {pendingSave && <p role="alert" className="mb-3 rounded border p-3 text-sm">{saveConflict ? 'Il tentativo non coincide con quello già registrato. Non riprovare: verifica la nota nella scheda originale.' : 'Il salvataggio precedente potrebbe essere riuscito. Verifica e riprova con lo stesso testo e destinazione; nessun invio parte automaticamente.'}</p>}
          {travelMode && <div className="mb-4 grid gap-3">
            <label className="text-sm font-medium">Wallet viaggio
              <select aria-label="Wallet per la nota rapida" disabled={isSaving || Boolean(pendingSave)} className="mt-1 min-h-11 w-full rounded-md border border-input bg-background px-3" value={walletId ?? ''} onChange={(event) => { setWalletId(event.target.value ? Number(event.target.value) : null); setTravelItemId(null); setNeedsTravelTargetChoice(false); setTravelError(''); }}>
                <option value="">Scegli un Wallet</option>
                {wallets.map((wallet) => <option key={wallet.id} value={wallet.id}>{wallet.title}{wallet.status === 'closed' ? ' · concluso' : ''}</option>)}
              </select>
            </label>
            <label className="text-sm font-medium">Tipo
              <select aria-label="Tipo nota rapida viaggio" disabled={isSaving || Boolean(pendingSave)} className="mt-1 min-h-11 w-full rounded-md border border-input bg-background px-3" value={travelKind} onChange={(event) => setTravelKind(event.target.value as 'note' | 'todo')}>
                <option value="note">Nota</option><option value="todo">Da fare</option>
              </select>
            </label>
            <label className="text-sm font-medium">Tappa
              <select aria-label="Tappa per la nota rapida" disabled={isSaving || Boolean(pendingSave)} className="mt-1 min-h-11 w-full rounded-md border border-input bg-background px-3" value={needsTravelTargetChoice ? '__choose__' : travelItemId ?? ''} onChange={(event) => { setTravelItemId(event.target.value ? Number(event.target.value) : null); setNeedsTravelTargetChoice(false); setTravelError(''); }}>
                {needsTravelTargetChoice && <option value="__choose__">Scegli di nuovo la destinazione</option>}
                <option value="">Intero viaggio</option>
                {wallets.find((wallet) => wallet.id === walletId)?.items.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
              </select>
            </label>
            {travelError && <p role="alert" className="text-sm text-destructive">{travelError}</p>}
          </div>}
          {!travelMode && <div className="mb-4 grid gap-3">
            {historyText !== null && !notebookMode && <section aria-label="Conversazione note azienda" className="rounded-xl border border-border bg-muted/20 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Note precedenti</p>
              <div className="max-h-52 overflow-y-auto whitespace-pre-wrap break-words rounded-xl rounded-bl-sm border border-border bg-card p-3 text-sm leading-6 text-foreground">
                {historyText || <span className="text-muted-foreground">Nessuna nota ancora registrata. Scrivi o detta la prima.</span>}
              </div>
            </section>}
            {notebookMode && <p className="text-sm text-muted-foreground">Nota personale nel Taccuino: salvi il testo originale. Non vengono creati Task, appuntamenti o collegamenti automaticamente.</p>}
            {sourceFile && <p className="rounded-md border border-border bg-muted/40 p-3 text-sm break-words">
              Documento di origine: <strong>{sourceFile.name}</strong>. La nota conterrà il link al file CRM.
            </p>}
            <label className="text-sm font-medium">Destinazione della nota
              <select aria-label="Tipo di record per la nota" disabled={isSaving || Boolean(pendingSave)} className="mt-1 min-h-11 w-full rounded-md border border-input bg-background px-3" value={notebookMode ? 'notebook' : entityType} onChange={(event) => { const notebook = event.target.value === 'notebook'; setNotebookMode(notebook); if (!notebook) setEntityType(event.target.value as NoteEntityContext['entityType']); setContext(null); }}>
                <option value="notebook">Taccuino Martina</option>
                {NOTE_ENTITY_TYPES.map((type) => <option key={type} value={type}>{ENTITY_LABEL[type]}</option>)}
              </select>
            </label>
            {!notebookMode && sourceFile && sourceFile.targets.length > 1 && <label className="text-sm font-medium">Record collegati al documento
              <select aria-label="Record collegato al documento" disabled={isSaving || Boolean(pendingSave)} className="mt-1 min-h-11 w-full rounded-md border border-input bg-background px-3" value={context ? `${context.entityType}:${context.entityId}` : ''} onChange={(event) => {
                const target = sourceFile.targets.find((item) => `${item.entityType}:${item.entityId}` === event.target.value);
                setContext(target ?? null);
                if (target) setEntityType(target.entityType);
              }}>
                <option value="">Scegli un record</option>
                {sourceFile.targets.map((target) => <option key={`${target.entityType}:${target.entityId}`} value={`${target.entityType}:${target.entityId}`}>{ENTITY_LABEL[target.entityType]} · {target.label || `#${target.entityId}`}</option>)}
              </select>
            </label>}
            {!notebookMode && <CrmRecordAutocomplete
              disabled={isSaving || Boolean(pendingSave)}
              entityType={entityType}
              value={context?.entityType === entityType ? String(context.entityId) : ''}
              selectedLabel={sourceFile?.targets.find((target) => target.entityType === context?.entityType && target.entityId === context.entityId)?.label}
              onValueChange={(value) => { const id = Number(value); setContext(Number.isSafeInteger(id) && id > 0 ? validNoteEntityContext({ entityType, entityId: id }) : null); }}
              placeholder={`Cerca ${ENTITY_LABEL[entityType].toLowerCase()}...`}
              searchPlaceholder="Cerca il record nel CRM..."
            />}
            {!notebookMode && !context && <p className="text-xs text-muted-foreground">Puoi dettare subito; scegli il record prima di salvare.</p>}
          </div>}
          {open && (
            <VoiceAssistantTextarea
              disabled={isSaving || Boolean(pendingSave)}
              key={captureSession}
              value={draft}
              onValueChange={(value) => { if (!savingRef.current && !pendingSave && ownerId === currentOwner()) setDraft(value); }}
              autoStartVoice={startVoice}
              mentions
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault();
                  void handleSave();
                }
              }}
              placeholder="Scrivi una nota da ricordare..."
              className="min-h-48 resize-y text-sm"
              aria-label="Nuova nota rapida"
              fieldLabel={context ? `Nota rapida ${context.entityType}` : travelMode ? 'Nota rapida Wallet viaggio' : 'Nota rapida per Martina'}
              martinaContext={context ? {
                terminalType: 'entity',
                entityType: context.entityType,
                entityId: String(context.entityId),
                route: context.route,
              } satisfies MartinaTerminalContext : undefined}
            />
          )}
          </>}
        </div>
        <SheetFooter className="shrink-0 gap-2 border-t border-border bg-card px-6 py-4 sm:justify-end">
          <Button type="button" variant="outline" disabled={isSaving} onClick={() => requestSafeClose(false)}>Annulla</Button>
          <Button type="button" variant="ghost" className="border border-emerald-300 bg-emerald-100 text-emerald-950 hover:bg-emerald-200" onClick={() => void handleSave()} disabled={isSaving || saveConflict || Boolean(recovery) || recoveryError || !draft.trim() || (travelMode ? !walletId || needsTravelTargetChoice : !notebookMode && !context)}>
            {isSaving ? 'Salvataggio...' : pendingSave ? 'Verifica e riprova salvataggio' : travelMode && travelKind === 'todo' ? 'Aggiungi da fare' : notebookMode ? 'Salva nel Taccuino' : 'Aggiungi nota'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
